import equal from 'fast-deep-equal';
import { useState, useCallback, useLayoutEffect, useRef } from 'react';
// Adapted from https://github.com/rehooks/component-size/blob/master/index.js
import ResizeObserver from 'resize-observer-polyfill';

/**
 * A representation of an element's size
 */
export interface ElementSize {
  /**
   * Width of element in pixels
   */
  width: number;
  /**
   * Height of element in pixels
   */
  height: number;
  /**
   * X Coordinate of element in pixels
   */
  x: number;
  /**
   * Y Coordinate of element in pixels
   */
  y: number;
  /**
   * Distance Left of element in pixels
   */
  left: number;
  /**
   * Distance Right of element in pixels
   */
  right: number;
  /**
   * Distance Top of element in pixels
   */
  top: number;
  /**
   * Distance Bottom of element in pixels
   */
  bottom: number;
}

/**
 * Recalculates the elements current size
 */
export type getCurrentSize = () => ElementSize;

function getSize(el?: Element) {
  if (!el) {
    return {
      width: 0,
      height: 0,
      x: 0,
      y: 0,
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    };
  }

  const { width, height, x, y, left, right, top, bottom } = el.getBoundingClientRect();

  return {
    width,
    height,
    x,
    y,
    left,
    right,
    top,
    bottom
  };
}
/**
 * Hook to get an element's size just attach a ref to read the size from the element
 * @returns {[ref,ElementSize,Function]} The ref to apply, the elements size, fn to get current size
 */
export default function useRect<T extends Element>(): [
  React.MutableRefObject<T | undefined>,
  ElementSize,
  getCurrentSize
] {
  const ref = useRef<T | undefined>();
  const [size, setComponentSize] = useState(() => getSize(ref.current));
  const handleResize = useCallback(() => {
    if (ref.current) {
      const newState = getSize(ref.current);
      setComponentSize(state => {
        if (equal(state, newState)) {
          return state;
        }
        return newState;
      });

      return newState;
    }

    return getSize();
  }, []);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    handleResize();
    // @ts-ignore
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    try {
      if (ref.current) {
        resizeObserver.observe(ref.current);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return;
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  return [ref, size, handleResize];
}
